import ScheduledLightChannels from '../components/ScheduledLightChannels';
import LightTimeChangeDialog from '../components/LightTimeChangeDialog';
import {
  supportsLightChannels,
  supportsScheduledLightChannels,
  scheduleLightWarnings,
} from '../Utils/light_profiles';
import { ActionButton } from '../components/ActionButton';
import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import Alert from '@material-ui/lab/Alert';
import { useSnapshot } from 'valtio';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import getStateManager from '../StateManager';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import TabPanel from '@material-ui/lab/TabPanel';
import { AppBar, Tab } from '@material-ui/core';

import _ from 'lodash';

import { parse, stringify } from 'yaml';

import {
  HandleAction,
  cloneSchedulePhase,
  isZoned,
  makePhase,
  makePhaseFromZoneSettings
} from '../Utils/utils.js';
import { SCHEDULE_PROTOCOL_VERSION } from '../Constants/constants.js';
import useSchedulingEnabled from '../Hooks/useSchedulingEnabled';

import { ShowSetting } from '../FormWidgets/ShowSliderSetting';
import MuiSwitch from '../FormWidgets/SettingsSwitch';
import { CircularIndeterminate } from '../FormWidgets/Others';
import {
  SettingGroup,
  SettingGroupBlock,
  ExpandableSettingsGroup,
  SettingGroupGrid,
  StartCollapsedContext,
  ExpandableSettingsGroupGrid,
  ValueBox
} from '../FormWidgets/SettingGroup';
import {
  AbsoluteRelativeSelect,
  FlexSelect,
  EventGapSelect,
  DayWeekSelect,
  DayWeekToggle,
  EnableDisableDaytimeSelect,
  EnableDayTimeDisableSelect
} from '../FormWidgets/Select.js';
import { MyTimePicker } from '../FormWidgets/DatePicker.js';
import { EditableLabel } from '../FormWidgets/EditableLabel.js';
import Desc from '../FormWidgets/Description';

import {
  scheduleShortNameLimit,
  supportsScheduleProtocol,
  supportsScheduling,
  validScheduleName,
  validScheduleNames,
  normalizeScheduleSettings,
  scheduledDayRange,
  nextSchedulePhaseId,
  schedulingSettingsReady,
  linkedSchedulePhase,
  scheduleFillingTanks
} from '../Utils/scheduling_utils';
import { co2TemperatureSettings, supportsCo2SettingsRelocation } from '../Utils/co2_utils';

import { SettingActions } from '../components';
import ScheduleNavigator, { ScheduleGroup, scrollToScheduleSection } from './Scheduling/ScheduleNavigator';
import { ScheduledCirculation, ScheduledTankFilling } from './Scheduling/ScheduledAuxiliarySettings';

import { EventTimerIndustrialPro, IntervalTimerIndustrialPro } from './TimingSettings.js';
import { StaticEventTabs, StaticIntervalTabs } from './StaticTimingTabs.js';
import LightChannelTabs from './LightChannelTabs.js';
import IndexedSettingsTabs from '../components/IndexedSettingsTabs.js';
import GrowCycleDayDialog from './GrowCycleDayDialog.js';
import ScheduleSelection, { filterScheduleItemsByBitmap } from './ScheduleSelection.js';
import IrrigationPhaseSettings, { IrrigationMoreSettings, IrrigationReferenceInterval } from './Irrigation/IrrigationPhaseSettings';
import IrrigationEventsEditor, {
  GCSPI_IRRIGATION_TIME_PHASE_P1,
  GCSPI_IRRIGATION_TIME_PHASE_P2
} from '../components/IrrigationEventsEditor.js';
import {
  isIrrigationAutoStrategy,
  irrigationWaitMinimumMinutes,
  supportsAdaptiveIrrigation,
  isIrrigationAdvancedVwcStrategy,
  isIrrigationVwcSimpleStrategy,
  supportsIrrigationReferenceInterval,
  isIrrigationTimeStrategy,
  isIrrigationVwcStrategy
} from '../Utils/irrigation_utils.js';
import { useIsMobile } from '../Hooks/useMedia.js';
import { isTankEnabled } from '../Utils/tank_availability';
import { Pump, hasInvalidNutrientSettingsUnit } from './NutrientSettings.js';

import useScheduleDraft from './Scheduling/useScheduleDraft';
import { ScheduleDraftRestore } from './Scheduling/ScheduledSettings';

export { schedulingSettingsReady, transformScheduleValuesForSend } from '../Utils/scheduling_utils';

function controllerSchedulePhase(parent, phase) {
  return parent.settings.scheduling.schedule_values.scheduling_values.find(
    (saved) => saved.phase_id === phase.phase_id) ?? makePhase({ phase_id: phase.phase_id });
}

export function buildScheduleExport(values) {
  return _.cloneDeep({
    schedule_meta: values.schedule_meta,
    schedule_values: values.schedule_values
  });
}

export function stringifyScheduleYaml(values) {
  return stringify(buildScheduleExport(values), { sortMapEntries: true });
}

export function parseScheduleYaml(yamlContent) {
  const parsed = parse(yamlContent);
  if (
    !parsed ||
    typeof parsed.schedule_meta !== 'object' ||
    parsed.schedule_meta?.protocol_version !== SCHEDULE_PROTOCOL_VERSION ||
    typeof parsed.schedule_values !== 'object' ||
    !Array.isArray(parsed.schedule_values.scheduling_values)
  ) {
    throw new Error('Invalid scheduling YAML structure');
  }

  const imported = buildScheduleExport(parsed);
  imported.schedule_values.scheduling_values.forEach((phase) => {
    phase.irrigation_event_count = phase.irrigation_events.length;
  });
  imported.schedule_values.schedule_values_count =
    imported.schedule_values.scheduling_values.length;
  imported.schedule_meta.active_phase_cnt = imported.schedule_values.scheduling_values.length;
  return imported;
}

export { submitScheduleSettings } from '../Utils/scheduling_transaction';

export default function Scheduling({ ...other }) {
  const { t } = useTranslation();
  const params = useParams();
  const schedulingEnabled = useSchedulingEnabled();
  const { controller, parent } = getStateManager().getControllerForTab(params);
  if (!supportsScheduling(controller)) return null;
  if (!schedulingEnabled) return (
    <Box role="status" p={3} bgcolor="background.paper" color="text.primary">
      <Typography>{t('schedule_unavailable')}</Typography>
    </Box>
  );
  return (
    <SchedulingReadback key={`${controller.id}/${parent.id}`} parent={parent} {...other} />
  );
}

function SchedulingLoading() {
  const { t } = useTranslation();
  return (
    <Box role="status" display="flex" flexDirection="column" alignItems="center" py={3}>
      <CircularIndeterminate />
      <Box mt={2}>
        <Typography>{t('schedule_loading')}</Typography>
      </Box>
    </Box>
  );
}

function SchedulingReadback({ parent, ...other }) {
  const { t } = useTranslation();
  const settings = useSnapshot(parent.settings);
  const readbackReady = schedulingSettingsReady(settings.scheduling);
  const [hasReadback, setHasReadback] = useState(false);
  useEffect(() => {
    if (readbackReady) setHasReadback(true);
  }, [readbackReady]);
  if (settings.scheduling?.schedule_meta &&
      settings.scheduling.schedule_meta.protocol_version !== SCHEDULE_PROTOCOL_VERSION)
    return <Alert severity="warning">{t('schedule_protocol_unsupported')}</Alert>;
  // Mount the draft/editor lifecycle only after authoritative readback arrives.
  // In particular, an old retained draft must not make a missing recipe editable.
  // Keep that lifecycle mounted during subsequent split updates: remounting it
  // would apply the user's entry-time restore/discard preference to active edits.
  if (!hasReadback && !readbackReady) return <SchedulingLoading />;
  return <SchedulingEditor readbackReady={readbackReady} {...other} />;
}

function SchedulingEditor({ readbackReady }) {
  const location = useLocation();
  const history = useHistory();
  const navigationTimer = useRef();
  const fileReader = useRef();
  useEffect(() => () => {
    clearTimeout(navigationTimer.current);
    fileReader.current?.abort();
    fileReader.current = undefined;
  }, []);
  const { t } = useTranslation();
  const params = useParams();
  const stateManager = getStateManager();
  const { controller, parent } = stateManager.getControllerForTab(params);
  const recipe = useScheduleDraft({ controller, parent });
  const { values, controls, submission } = recipe;
  const settings = useSnapshot(parent.settings);
  const setValues = (next) => controls.setValue(null, typeof next === 'function' ? next(values) : next);
  const { access_rights: accessRights } = useSnapshot(controller);
  const writeAccess = Boolean(accessRights?.scheduling?.write);
  const fields = {
    component: 'fieldset',
    disabled: !writeAccess,
    style: { border: 0, margin: 0, padding: 0, minWidth: 0 }
  };
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const schedulePhases = Array.isArray(values?.schedule_values?.scheduling_values)
    ? values.schedule_values.scheduling_values
    : [];
  const schedulingReady =
    values?.schedule_meta != null && Array.isArray(values?.schedule_values?.scheduling_values);
  let num_phases = schedulePhases.length;
  const hasInvalidPumpUnit = schedulePhases
    .filter((phase) => !phase._deleted)
    .some((phase) => hasInvalidNutrientSettingsUnit(phase.nutrient_settings));

  const [useWeekDay, setUseWeekDay] = useState(
    Array.from(
      { length: num_phases },
      (_, i) => values?.schedule_meta?.['len_phase_' + (i + 1) + '_days'] % 7 === 0
    )
  );
  const [growCycleDayDialogOpen, setGrowCycleDayDialogOpen] = useState(false);

  const callAction = (type, value) => {
    if (!controller.access_rights?.scheduling?.write) return;
    const zone_id = isZoned(controller) ? parent.id : undefined;
    HandleAction(stateManager.wsManager, controller, type, value, zone_id);
  };

  const ToggleDayWeek = (index, value) => {
    if (!writeAccess) return;
    // Update use week/day array:.
    setUseWeekDay((current) => current.map((entry, i) => (i === index ? value : entry)));
    // modify number of days matching days/ weeks
    const id = 'len_phase_' + (index + 1) + '_days';
    // Update value in schedule-meta-struct
    controls.setValue(
      `schedule_meta[${id}]`,
      Math.min(value ? 252 : 255, values.schedule_meta[id] * (value ? 7 : 1 / 7))
    );
  };

  const [affectedLights, setAffectedLights] = useState([]);
  const commitSchedule = () => { if (!hasInvalidPumpUnit) recipe.save(); };
  const save = () => {
    const affected = supportsLightChannels(controller)
      ? scheduleLightWarnings(controller.settings.light_channels, Number(parent.zone_id), values, parent.settings.timing)
      : [];
    if (affected.length) setAffectedLights(affected);
    else commitSchedule();
  };
  const resetSchedule = recipe.reset;

  const addPhase = () => {
    if (!writeAccess) return;
    const durationIndex = values.schedule_values.scheduling_values.length + 1;
    const phase_id = nextSchedulePhaseId(values.schedule_values.scheduling_values);
    const previousPhase = [...values.schedule_values.scheduling_values]
      .reverse()
      .find((phase) => !phase._deleted);
    const phase = previousPhase
      ? cloneSchedulePhase(previousPhase, phase_id)
      : makePhaseFromZoneSettings(
          parent.settings,
          phase_id,
          'Your Name',
          co2TemperatureSettings(controller, parent).value ?? parent.settings.co2?.temp_sp_co2,
          scheduleFillingTanks(parent)
        );
    const _phases = [...values.schedule_values.scheduling_values];
    _phases.push(phase);
    //setPhases(phases);

    controls.setValue(
      [
        'schedule_meta.active_phase_cnt',
        `schedule_meta.len_phase_${durationIndex}_days`,
        'schedule_values.scheduling_values'
      ],
      [values.schedule_meta.active_phase_cnt + 1, 14, _phases]
    );
    setUseWeekDay((current) => [...current, true]);
  };

  const delPhase = (index) => {
    if (!writeAccess) return;
    // Empty phases and reduce number of active phases

    const _phases = values.schedule_values.scheduling_values.map((phase, i) =>
      i === index ? { ...phase, _deleted: true } : phase
    );
    controls.setValue(
      ['schedule_meta.active_phase_cnt', 'schedule_values.scheduling_values'],
      [values.schedule_meta.active_phase_cnt - 1, _phases]
    );
  };

  const visiblePhases = schedulePhases.filter((phase) => !phase._deleted);
  const ordinal = (phase) =>
    visiblePhases.findIndex((candidate) => candidate.phase_id === phase.phase_id) + 1;
  let elapsedDays = 0;
  const phaseRanges = schedulePhases.map((phase, index) => {
    if (phase._deleted) return null;
    const first = elapsedDays + 1;
    elapsedDays += Number(values.schedule_meta[`len_phase_${index + 1}_days`]);
    return {
      days: `${first} - ${elapsedDays}`,
      weeks: `${Math.ceil(first / 7)} - ${Math.ceil(elapsedDays / 7)}`
    };
  });

  // Navigation selects a stable phase identity; drafts belong to the recipe,
  // so changing the visible phase or group never restores or discards edits.
  const [selectedPhaseId, setSelectedPhaseId] = useState(null);
  const [preferredGroup, setPreferredGroup] = useState('climate');
  const selectedPhase = visiblePhases.find((phase) => phase.phase_id === selectedPhaseId)
    || visiblePhases[0];
  const selectedId = selectedPhase?.phase_id;
  const meta = values.schedule_meta || {};
  const co2InClimate = supportsCo2SettingsRelocation(controller);
  const groups = [
    ['climate', meta.schedule_humidity_enabled || meta.schedule_main_temperature_enabled ||
      meta.schedule_vpd_enabled || meta.schedule_heating_mat_enabled ||
      (co2InClimate && meta.schedule_co2_temperature_enabled)],
    ['lights', meta.schedule_lights_enabled],
    ['irrigation', meta.schedule_irrigations_enabled],
    ['nutrition', meta.schedule_nutrients_enabled || meta.schedule_tank_filling_enabled],
    ['timing', meta.schedule_day_night_enabled || meta.schedule_intervals_enabled || meta.schedule_events_enabled],
    ['co2', meta.schedule_co2_concentration_enabled || (!co2InClimate && meta.schedule_co2_temperature_enabled)],
    ['ventilation', meta.schedule_circulation_enabled]
  ].filter(([, enabled]) => Boolean(enabled)).map(([group]) => group);
  const selectedGroup = groups.includes(preferredGroup) ? preferredGroup : groups[0];
  const navigateToPhase = (phaseId, group = selectedGroup) => {
    setSelectedPhaseId(phaseId);
    if (group) setPreferredGroup(group);
    // Wait until React has mounted the selected phase's controls.
    clearTimeout(navigationTimer.current);
    navigationTimer.current = setTimeout(() => {
      const target = document.getElementById(`schedule-phase-${phaseId}-${group}`)
        || document.getElementById(`schedule-phase-${phaseId}`);
      scrollToScheduleSection(target);
    }, 0);
  };

  const download = () => {
    const yaml = stringifyScheduleYaml(values);
    // Create a Blob from the YAML string
    const blob = new Blob([yaml], { type: 'text/yaml' });
    // Create a link element and set the download attribute with a filename
    const link = document.createElement('a');
    link.download = values.schedule_meta.name_short + '.yaml';
    // Create a URL for the Blob and set it as the href attribute
    link.href = URL.createObjectURL(blob);
    // Programmatically click the link to trigger the download
    link.click();
    // Clean up the URL object
    URL.revokeObjectURL(link.href);
  };

  const [yamlContent, setYamlContent] = useState(null);
  const [fileName, setFileName] = useState('');
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setYamlContent(null);
    setFileName(file?.name ?? '');
    fileReader.current?.abort();
    fileReader.current = undefined;

    if (file) {
      const reader = new FileReader();
      fileReader.current = reader;
      reader.onload = (e) => {
        if (fileReader.current === reader) setYamlContent(e.target.result);
      };
      reader.readAsText(file);
    }
  };
  const upload = () => {
    if (!writeAccess) return;
    if (yamlContent) {
      try {
        // Parse the YAML content to a JSON object
        const jsonObject = parseScheduleYaml(yamlContent);
        // Imported content starts a draft against this controller's readback.
        jsonObject.schedule_meta.recipe_revision = settings.scheduling.schedule_meta.recipe_revision;
        jsonObject.schedule_values.recipe_revision = settings.scheduling.schedule_values.recipe_revision;
        controls.setValue(
          ['schedule_meta', 'schedule_values', '_editing_phase'],
          [normalizeScheduleSettings(jsonObject).schedule_meta, jsonObject.schedule_values, undefined]
        );
        setUseWeekDay(
          Array.from(
            { length: jsonObject.schedule_values.scheduling_values.length },
            (_, i) => jsonObject.schedule_meta['len_phase_' + (i + 1) + '_days'] % 7 === 0
          )
        );
      } catch (error) {
        console.error('Error parsing YAML:', error);
      }
    } else {
      alert('No File selected.');
    }
  };

  const linkedIndex = linkedSchedulePhase(settings.scheduling, location.search);
  const linkedPhaseId = settings.scheduling?.schedule_values?.scheduling_values[linkedIndex]?.phase_id;
  const linkedDraftIndex = linkedPhaseId === undefined ? -1 : schedulePhases.findIndex(
    (phase) => !phase._deleted && phase.phase_id === linkedPhaseId
  );
  const phaseLinkChanged = linkedIndex !== undefined && linkedDraftIndex < 0;
  const clearPhaseLink = () => {
    const search = new URLSearchParams(location.search);
    search.delete('phase');
    search.delete('recipe');
    history.replace({ ...location, search: search.toString() ? `?${search}` : '' });
  };
  useEffect(() => {
    if (!readbackReady || linkedDraftIndex < 0) return;
    setSelectedPhaseId(linkedPhaseId);
    // A link is an entry-time jump, not a persistent restriction on later saves.
    const search = new URLSearchParams(location.search);
    search.delete('phase');
    search.delete('recipe');
    history.replace({ ...location, search: search.toString() ? `?${search}` : '' });
    clearTimeout(navigationTimer.current);
    navigationTimer.current = setTimeout(() => scrollToScheduleSection(
      document.getElementById(`schedule-phase-${linkedPhaseId}`)), 0);
  }, [linkedPhaseId, linkedDraftIndex, readbackReady, history, location]);
  const showPhases = () => {
    clearPhaseLink();
    scrollToScheduleSection(document.getElementById('schedule-navigator'));
    document.querySelector('#schedule-navigator select')?.focus({ preventScroll: true });
  };

  if (!readbackReady || !schedulingReady) return <SchedulingLoading />;

  const unsupported = !supportsScheduleProtocol(controller, values);
  const shortNameLimit = scheduleShortNameLimit(controller, values);
  const invalidNames = !validScheduleNames(values, shortNameLimit);
  const validateShortName = (name) =>
    validScheduleName(name, shortNameLimit) ? '' : `schedule_short_name_invalid_${shortNameLimit}`;
  const saveDisabled =
    !writeAccess ||
    connectionStatus !== 'good' ||
    hasInvalidPumpUnit ||
    invalidNames ||
    unsupported ||
    ['pending', 'conflict'].includes(submission?.status) ||
    !schedulePhases.some((phase) => !phase._deleted);
  const runtimeStatus = Number(settings.scheduling.schedule_values.runtime_status ?? 0);


  return (
    <>
      <LightTimeChangeDialog affected={affectedLights}
        onCancel={() => setAffectedLights([])}
        onContinue={() => { setAffectedLights([]); commitSchedule(); }} />
      <ScheduleDraftRestore recipe={recipe} />
      <Typography variant="h5" component="h1"><Desc id="schedule_overview_hl" /></Typography>
      <GrowCycleDayDialog
        open={growCycleDayDialogOpen}
        currentDay={parent.infos?.cur_grow_cycle_day}
        disabled={!writeAccess || connectionStatus !== 'good'}
        onClose={() => setGrowCycleDayDialogOpen(false)}
        onSet={(day) => callAction('action_set_grow_cycle_day', day)}
      />

      {writeAccess && (
        <SettingActions
          title={connectionDisabledReason}
          onReset={resetSchedule}
          onSave={save}
          on_default={saveDisabled}
        />
      )}

      {phaseLinkChanged && (
        <Alert severity="info" action={<Button color="inherit" size="small" onClick={showPhases}>
          {t('schedule_show_phases')}
        </Button>}>
          {t('schedule_phase_link_changed')}
        </Alert>
      )}
      {unsupported && <Alert severity="warning">{t('schedule_protocol_unsupported')}</Alert>}
      {invalidNames && (
        <Alert severity="warning">{t(`schedule_names_invalid_${shortNameLimit}`)}</Alert>
      )}

      {runtimeStatus === 3 && (
        <Alert severity="warning">{t('schedule_runtime_invalid')}</Alert>
      )}



      <ExpandableSettingsGroup controller={controller} id="phases_hl" inlineHeader>
        <Grid container spacing={1}>
        {values.schedule_values.scheduling_values.map((phase, i) =>
          !phase._deleted ? (
            <Grid item xs={12} md={6} key={`phase-editor-${phase.phase_id}`}>
            <ValueBox>
              <Box {...fields}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <EditableLabel
                      label={t('phase') + ' ' + ordinal(phase)}
                      id={'phase_' + phase.phase_id}
                      disabled={!writeAccess}
                      value={phase.name_phase_short}
                      validate={validateShortName}
                      handleInput={(phase_id, event, value) =>
                        controls.setValue(
                          [
                            `schedule_values.scheduling_values[${i}].name_phase_short`,
                            `schedule_values.scheduling_values[${i}].name_phase_full`
                          ],
                          [value === -1 ? '' : value, value === -1 ? '' : value]
                        )
                      }
                    />
                  </Grid>
                  <Grid item xs={3} sm={2}>
                    <DayWeekSelect
                      use_week={useWeekDay[i]}
                      maxDays={255}
                      inputProps={{ 'aria-label': `phase_duration_${i}` }}
                      value={values.schedule_meta[`len_phase_${i + 1}_days`]}
                      controller_value={
                        parent.settings.scheduling.schedule_meta[`len_phase_${i + 1}_days`]
                      }
                      onChange={(event, value) =>
                        controls.setValue(
                          `schedule_meta.len_phase_${i + 1}_days`,
                          event.target.value
                        )
                      }
                      style={{ minWidth: 0, width: '100%' }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <DayWeekToggle
                      value={useWeekDay[i]}
                      onChange={(event, value) => ToggleDayWeek(i, event.target.value)}
                      style={{ minWidth: 0, width: '100%' }}
                    />
                  </Grid>
                  <Grid item xs={3} sm={2} style={{ textAlign: 'right' }}>
                    <Button
                      style={{ minWidth: 44, minHeight: 44, padding: 8 }}
                      color="primary"
                      onClick={() => delPhase(i)}
                      disabled={
                        !writeAccess ||
                        schedulePhases.filter((entry) => !entry._deleted).length <= 1
                      }
                    >
                      <DeleteForeverIcon />
                    </Button>
                  </Grid>
                </Grid>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap">
                  <Typography variant="body2">{t('day')}: {phaseRanges[i].days}</Typography>
                  <Button color="primary" size="small"
                    aria-label={`${t('schedule_edit_phase')} ${ordinal(phase)}`}
                    onClick={() => navigateToPhase(phase.phase_id)}>
                    {t('schedule_edit_phase')}
                  </Button>
                </Box>
              </Box>
            </ValueBox>
            </Grid>
          ) : null
        )}
        </Grid>
        <Box display="flex" flexWrap="wrap" gridGap={8} mt={2}>
          <Button
            color="primary"
            onClick={addPhase}
            disabled={!writeAccess || schedulePhases.length >= 10}
          >
            {t('add_phase')}
          </Button>
          <ActionButton
            title={connectionDisabledReason}
            disabled={!writeAccess || connectionStatus !== 'good'}
            color="primary"
            onClick={() => setGrowCycleDayDialogOpen(true)}
          >
            {t('set_current_day_in_grow_cycle')}
          </ActionButton>
        </Box>
      </ExpandableSettingsGroup>

      <ExpandableSettingsGroup controller={controller} id="selection_hl" inlineHeader>
        <Box {...fields}>
          <ScheduleSelection
            controller={controller}
            parent={parent}
            values={values}
            controls={controls}
          />
        </Box>
      </ExpandableSettingsGroup>

      <ScheduleNavigator
        phases={visiblePhases.map((phase) => ({
          id: phase.phase_id,
          number: ordinal(phase),
          name: phase.name_phase_full || phase.name_phase_short,
          days: phaseRanges[schedulePhases.indexOf(phase)].days
        }))}
        selectedPhaseId={selectedId}
        groups={groups}
        selectedGroup={selectedGroup}
        onNavigate={navigateToPhase}
        onGroupVisible={setPreferredGroup}
      />

      {schedulePhases.map((phase, i) =>
        !phase._deleted && phase.phase_id === selectedId ? (
          <StartCollapsedContext.Provider key={`phase-panel-${phase.phase_id}`} value={true}>
          <SettingGroupGrid
            label={`${t('phase')} ${ordinal(phase)}${phase.name_phase_full || phase.name_phase_short ? ` · ${phase.name_phase_full || phase.name_phase_short}` : ''}`}
            fullWidth={true}>
            <Grid container id={`schedule-phase-${phase.phase_id}`}>
              <Grid item xs={12} sm={6}>{t('day')}: {phaseRanges[i].days}</Grid>
              <Grid item xs={12} sm={6}>{t('week')}: {phaseRanges[i].weeks}</Grid>
            </Grid>
            <Box {...fields}>
              {groups.includes('climate') && <ScheduleGroup phaseId={phase.phase_id} group="climate">
                <HumiditySettings index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
                <TempSettings index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
                <VpdSettings index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
                <HeatingMatSettings index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
                {co2InClimate && <Co2TemperSettings index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />}
              </ScheduleGroup>}
              {groups.includes('lights') && <ScheduleGroup phaseId={phase.phase_id} group="lights">
                <DimChannelSettings index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
              </ScheduleGroup>}
              {groups.includes('irrigation') && <ScheduleGroup phaseId={phase.phase_id} group="irrigation">
                {controller.zones
                  ? <MultipleIrrigationSettingsTabs index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
                  : <MultipleIrrigationSettings id={0} index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />}
              </ScheduleGroup>}
              {groups.includes('nutrition') && <ScheduleGroup phaseId={phase.phase_id} group="nutrition">
                {meta.schedule_nutrients_enabled ? controller.zones
                  ? <MultipleNutrientSettingsTabs index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
                  : <NutrientSettings index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
                  : null}
                {meta.schedule_tank_filling_enabled ? <ScheduledTankFilling index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} /> : null}
              </ScheduleGroup>}
              {groups.includes('timing') && <ScheduleGroup phaseId={phase.phase_id} group="timing">
                <StartEndSettings index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
                {controller.type !== 6000
                  ? <ScheduledIntervalTabs t={t} index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
                  : <ScheduledDynamicIntervalTabs t={t} phaseIndex={i}
                      timers={parent.settings.timing.interval_timer} values={values}
                      setValues={setValues} controls={controls} controller={controller} />}
                {controller.type !== 6000
                  ? <ScheduledEventTabs t={t} index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
                  : <ScheduledDynamicEventTabs t={t} phaseIndex={i}
                      timers={parent.settings.timing.event_timer} values={values}
                      setValues={setValues} controls={controls} controller={controller} />}
              </ScheduleGroup>}
              {groups.includes('co2') && <ScheduleGroup phaseId={phase.phase_id} group="co2">
                <Co2ConcentrationSettings index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
                {!co2InClimate && <Co2TemperSettings index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />}
              </ScheduleGroup>}
              {groups.includes('ventilation') && <ScheduleGroup phaseId={phase.phase_id} group="ventilation">
                <ScheduledCirculation index={i} values={values} phase={phase} controls={controls} controller={controller} parent={parent} />
              </ScheduleGroup>}
              {!groups.length && <Typography>{t('schedule_no_groups')}</Typography>}
            </Box>
          </SettingGroupGrid>
          </StartCollapsedContext.Provider>
        ) : null
      )}

      <SettingGroupGrid controller={controller} id={'save_and_load_hl'}>
        <ValueBox controller={controller} id={'scheduling_name_short'}>
          <EditableLabel
            label={t('current_schedule_short_name')}
            id={'name_short'}
            disabled={!writeAccess}
            validate={validateShortName}
            value={values.schedule_meta.name_short}
            handleInput={(id, event, value) =>
              controls.setValue('schedule_meta.name_short', value === -1 ? '' : value)
            }
          />
        </ValueBox>
        <Box display="flex" flexDirection="column" alignItems="flex-start" gridGap={16} p={2}>
          <Button color="primary" onClick={download}>
            {t('download_current_schedule_to_file')}
          </Button>
          <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
            <Button component="label" color="primary" variant="outlined" disabled={!writeAccess}>
              {t('schedule_choose_file')}
              <input type="file" hidden disabled={!writeAccess} accept=".yaml,.yml"
                aria-label={t('schedule_choose_file')} onChange={handleFileChange} />
            </Button>
            {fileName && <Typography variant="body2">{fileName}</Typography>}
            <Button color="primary" onClick={upload} disabled={!writeAccess || !yamlContent}>
              {t('upload_schedule_from_file')}
            </Button>
          </Box>
        </Box>
      </SettingGroupGrid>

      {writeAccess && (
        <SettingActions
          title={connectionDisabledReason}
          onReset={resetSchedule}
          onSave={save}
          on_default={saveDisabled}
        />
      )}
    </>
  );
}

function HumiditySettings({ index, values, phase, controls, controller, parent }) {
  if (values.schedule_meta.schedule_humidity_enabled) {
    return (
      <SettingGroupGrid controller={controller} id={'humidity_sp'}>
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].humidity_sp_day`}
          show={'humidity_sp_day'}
          controller={controller}
          cur_value={phase.humidity_sp_day}
          controller_value={
            controllerSchedulePhase(parent, phase).humidity_sp_day
          }
          controls={controls}
        />
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].humidity_sp_night`}
          show={'humidity_sp_night'}
          controller={controller}
          cur_value={phase.humidity_sp_night}
          controller_value={
            controllerSchedulePhase(parent, phase)
                  .humidity_sp_night
          }
          controls={controls}
        />
      </SettingGroupGrid>
    );
  } else {
    return <></>;
  }
}

function TempSettings({ index, values, phase, controls, controller, parent }) {
  if (values.schedule_meta.schedule_main_temperature_enabled) {
    return (
      <SettingGroupGrid controller={controller} id={'temp_sp'}>
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].temp_sp_day`}
          show={'temp_sp_day'}
          controller={controller}
          cur_value={phase.temp_sp_day}
          controller_value={
            controllerSchedulePhase(parent, phase).temp_sp_day
          }
          controls={controls}
          type="float"
        />
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].temp_sp_night`}
          show={'temp_sp_night'}
          controller={controller}
          cur_value={phase.temp_sp_night}
          controller_value={
            controllerSchedulePhase(parent, phase).temp_sp_night
          }
          controls={controls}
          type="float"
        />
      </SettingGroupGrid>
    );
  } else {
    return <></>;
  }
}

function VpdSettings({ index, values, phase, controls, controller, parent }) {
  if (values.schedule_meta.schedule_vpd_enabled) {
    return (
      <SettingGroupGrid controller={controller} id={'vpd_sp'}>
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].vpd_sp`}
          show={'vpd_sp'}
          controller={controller}
          cur_value={phase.vpd_sp}
          controller_value={
            controllerSchedulePhase(parent, phase).vpd_sp
          }
          unit={controller.settings.system.vpd_format === 0 ? 'hPa' : 'kPa'}
          controls={controls}
          num_digits={controller.settings.system.vpd_format === 0 ? 1 : 2}
          mod_digits={controller.settings.system.vpd_format === 0 ? 1 : 10}
          type="float"
        />
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].vpd_sp_night`}
          show={'vpd_sp_night'}
          controller={controller}
          cur_value={phase.vpd_sp_night}
          controller_value={
            controllerSchedulePhase(parent, phase).vpd_sp_night
          }
          unit={controller.settings.system.vpd_format === 0 ? 'hPa' : 'kPa'}
          controls={controls}
          num_digits={controller.settings.system.vpd_format === 0 ? 1 : 2}
          mod_digits={controller.settings.system.vpd_format === 0 ? 1 : 10}
          type="float"
        />
        <ValueBox controller={controller} id={'scheduling_'}>
          <Typography gutterBottom>
            <Desc id="vpd_calc_enable" show="vpd_calc_enable" />
          </Typography>
          <EnableDisableDaytimeSelect
            value={phase.vpd_calc_enable}
            controller_value={
              controllerSchedulePhase(parent, phase)?.vpd_calc_enable
            }
            name="vpd_calc_enable"
            onChange={(event, value) =>
              controls.setValue(
                `schedule_values.scheduling_values[${index}].vpd_calc_enable`,
                event.target.value
              )
            }
          />
        </ValueBox>
      </SettingGroupGrid>
    );
  } else {
    return <></>;
  }
}

function HeatingMatSettings({ index, values, phase, controls, controller, parent }) {
  let { t } = useTranslation();

  if (values.schedule_meta.schedule_heating_mat_enabled) {
    return (
      <SettingGroupBlock label={t('temperature_setpoint_heating_mat')}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <ValueBox controller={controller} id={'scheduling_'}>
            <Typography gutterBottom>
              <Desc id="toggle_temp_heating_mat_day" />
            </Typography>
            <AbsoluteRelativeSelect
              value={phase.toggle_temp_heating_mat_day}
              controller_value={
                controllerSchedulePhase(parent, phase)
                  ?.toggle_temp_heating_mat_day
              }
              name="toggle_temp_heating_mat_day"
              onChange={(event, value) =>
                controls.setValue(
                  `schedule_values.scheduling_values[${index}].toggle_temp_heating_mat_day`,
                  event.target.value
                )
              }
            />
          </ValueBox>
          <ValueBox controller={controller} id={'scheduling_'}>
            <Typography gutterBottom>
              <Desc id="toggle_temp_heating_mat_night" />
            </Typography>
            <AbsoluteRelativeSelect
              value={phase.toggle_temp_heating_mat_night}
              controller_value={
                controllerSchedulePhase(parent, phase)
                  ?.toggle_temp_heating_mat_night
              }
              name="toggle_temp_heating_mat_night"
              onChange={(event, value) =>
                controls.setValue(
                  `schedule_values.scheduling_values[${index}].toggle_temp_heating_mat_night`,
                  event.target.value
                )
              }
            />
          </ValueBox>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'stretch' }}>
          {phase.toggle_temp_heating_mat_day === 1 ? (
            <ShowSetting
              id={`schedule_values.scheduling_values[${index}].temp_sp_heating_mat_day_abs`}
              show={'temp_sp_heating_mat_day_abs'}
              controller={controller}
              cur_value={phase.temp_sp_heating_mat_day_abs}
              controller_value={
                controllerSchedulePhase(parent, phase)
                      .temp_sp_heating_mat_day_abs
              }
              controls={controls}
              type="float"
            />
          ) : (
            <ShowSetting
              id={`schedule_values.scheduling_values[${index}].temp_sp_heating_mat_day_rel`}
              show={'temp_sp_heating_mat_day_rel'}
              controller={controller}
              cur_value={phase.temp_sp_heating_mat_day_rel}
              controller_value={
                controllerSchedulePhase(parent, phase)
                      .temp_sp_heating_mat_day_rel
              }
              controls={controls}
              type="float"
            />
          )}
          {phase.toggle_temp_heating_mat_night === 1 ? (
            <ShowSetting
              id={`schedule_values.scheduling_values[${index}].temp_sp_heating_mat_night_abs`}
              show={'temp_sp_heating_mat_night_abs'}
              controller={controller}
              cur_value={phase.temp_sp_heating_mat_night_abs}
              controller_value={
                controllerSchedulePhase(parent, phase)
                      .temp_sp_heating_mat_night_abs
              }
              controls={controls}
              type="float"
            />
          ) : (
            <ShowSetting
              id={`schedule_values.scheduling_values[${index}].temp_sp_heating_mat_night_rel`}
              show={'temp_sp_heating_mat_night_rel'}
              controller={controller}
              cur_value={phase.temp_sp_heating_mat_night_rel}
              controller_value={
                controllerSchedulePhase(parent, phase)
                      .temp_sp_heating_mat_night_rel
              }
              controls={controls}
              type="float"
            />
          )}
        </Box>
      </SettingGroupBlock>
    );
  } else {
    return <></>;
  }
}

function Co2ConcentrationSettings({ index, values, phase, controls, controller, parent }) {
  if (values.schedule_meta.schedule_co2_concentration_enabled) {
    return (
      <SettingGroupGrid controller={controller} id={'co2_concentration'}>
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].co2_sp`}
          show={'co2_sp'}
          controller={controller}
          cur_value={phase.co2_sp}
          controller_value={
            controllerSchedulePhase(parent, phase).co2_sp
          }
          controls={controls}
        />
        <ValueBox controller={controller} id={'scheduling_'}>
          <Typography>
            <Desc id="co2_control" />
          </Typography>
          <EnableDayTimeDisableSelect
            value={phase.co2_control}
            controller_value={
              controllerSchedulePhase(parent, phase)?.co2_control
            }
            name="co2_control"
            onChange={(event) =>
              controls.setValue(
                `schedule_values.scheduling_values[${index}].co2_control`,
                event.target.value
              )
            }
          />
        </ValueBox>
      </SettingGroupGrid>
    );
  } else {
    return <></>;
  }
}

function Co2TemperSettings({ index, values, phase, controls, controller, parent }) {
  if (values.schedule_meta.schedule_co2_temperature_enabled) {
    return (
      <SettingGroupGrid controller={controller} id={'temperature_co2'}>
        <ShowSetting
          id={`schedule_values.scheduling_values[${index}].temp_sp_co2`}
          show={'temp_sp_co2'}
          controller={controller}
          cur_value={phase.temp_sp_co2}
          controller_value={
            controllerSchedulePhase(parent, phase).temp_sp_co2
          }
          controls={controls}
          type="float"
        />
      </SettingGroupGrid>
    );
  } else {
    return <></>;
  }
}

export function StartEndSettings({ index, values, phase, controls, controller }) {
  if (values.schedule_meta.schedule_day_night_enabled) {
    return (
      <SettingGroup controller={controller} id={'start_end_time'}>
        <ValueBox controller={controller} id={'scheduling_'}>
          <Typography gutterBottom>
            <Desc id="start_day" show="start_day" />
          </Typography>
          <MyTimePicker
            value={phase.time_day_start}
            seconds={false}
            name="start_day"
            onChange={(value) =>
              controls.setValue(
                `schedule_values.scheduling_values[${index}].time_day_start`,
                value.toLocaleTimeString('de-DE')
              )
            }
          />
        </ValueBox>
        <ValueBox controller={controller} id={'scheduling_'}>
          <Typography gutterBottom>
            <Desc id="end_day" show="end_day" />
          </Typography>
          <MyTimePicker
            value={phase.time_day_end}
            seconds={false}
            name="end_day"
            onChange={(value) =>
              controls.setValue(
                `schedule_values.scheduling_values[${index}].time_day_end`,
                value.toLocaleTimeString('de-DE')
              )
            }
          />
        </ValueBox>
      </SettingGroup>
    );
  } else {
    return <></>;
  }
}

export function DimChannelSettings({ index, values, phase, controls, controller, parent }) {
  const { t } = useTranslation();
  const channelSchedulingSupported = supportsScheduledLightChannels(
    controller,
    values.schedule_meta.protocol_version
  );
  if (channelSchedulingSupported)
    return (
      <ScheduledLightChannels
        {...{ index, values, phase, controls, controller, parent }}
        recoveryOnly={!values.schedule_meta.schedule_lights_enabled}
      />
    );
  if (values.schedule_meta.protocol_version === 7 && phase.lighting?.schema_version === 1)
    return <Alert severity="info">{t('lighting_phase_installation_unavailable')}</Alert>;
  if (!values.schedule_meta.schedule_lights_enabled) return <></>;
  const lightIds = filterScheduleItemsByBitmap(
    [1, 2, 3, 4],
    values.schedule_meta.schedule_lights_bitmap,
    (lightId) => lightId - 1
  );
  return (
    <LightChannelTabs
      ids={lightIds}
      t={t}
      idPrefix={`phase-${index}-light`}
      controller={controller}
      controls={controls}
      appBarStyle={{ margin: '24px 24px 0', width: 'calc(100% - 48px)' }}
      groupId="lights"
      getFieldId={(id) => `schedule_values.scheduling_values[${index}].lights_max_${id}`}
      getValue={(id) => phase[`lights_max_${id}`]}
      getControllerValue={(id) =>
        controllerSchedulePhase(parent, phase)?.[`lights_max_${id}`] ??
        makePhase({ phase_id: index })[`lights_max_${id}`]
      }
    />
  );
}

function MultipleNutrientSettingsTabs({ index, values, phase, controls, controller, parent }) {
  const { t } = useTranslation();
  const nutrientIds = filterScheduleItemsByBitmap(
    Object.keys(phase.nutrient_settings || {}),
    values.schedule_meta.schedule_nutrients_bitmap,
    (nutrientId) => Number(nutrientId)
  );
  const [activeNutrient, setActiveNutrient] = React.useState(nutrientIds[0] || '');
  React.useEffect(() => {
    if (!nutrientIds.includes(activeNutrient) && nutrientIds.length) {
      setActiveNutrient(nutrientIds[0]);
    }
  }, [nutrientIds, activeNutrient]);

  if (values.schedule_meta.schedule_nutrients_enabled && nutrientIds.length > 0) {
    return (
      <TabContext value={activeNutrient}>
        <AppBar
          position="static"
          color="default"
          style={{ margin: '24px 24px 0', width: 'calc(100% - 48px)' }}
        >
          <Box display="flex" justifyContent="center" width="100%">
            <TabList
              variant="scrollable"
              scrollButtons="auto"
              onChange={(_, v) => setActiveNutrient(v)}
            >
              {nutrientIds.map((nid, idx) => (
                <Tab
                  key={nid}
                  value={nid}
                  label={`${t('sub_zone')} ${Number(nid) + 1}`}
                  id={`phase-${index}-nutrient-tab-${idx}`}
                  aria-controls={`phase-${index}-nutrient-tabpanel-${idx}`}
                />
              ))}
            </TabList>
          </Box>
        </AppBar>

        {nutrientIds.map((nid, idx) => (
          <TabPanel
            key={nid}
            value={nid}
            id={`phase-${index}-nutrient-tabpanel-${idx}`}
            style={{ padding: 0 }}
          >
            <MultipleNutrientSettings
              id={Number(nid)}
              index={index}
              values={values}
              phase={phase}
              controls={controls}
              controller={controller}
              parent={parent}
            />
          </TabPanel>
        ))}
      </TabContext>
    );
  } else {
    return <></>;
  }
}

export function MultipleNutrientSettings({
  id,
  index,
  values,
  phase,
  controls,
  controller,
  parent
}) {
  if (values.schedule_meta.schedule_nutrients_enabled) {
    return (
      <>
        <SettingGroupGrid controller={controller} id={'nutrient_solution'}>
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[${id}].ec_setpoint`}
            show={'ec_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[id].ec_setpoint}
            controller_value={
              controllerSchedulePhase(parent, phase)
                    .nutrient_settings[id].ec_setpoint
            }
            controls={controls}
            disabled={!controller.access_rights?.scheduling?.write}
            num_digits={2}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[${id}].ph_setpoint`}
            show={'ph_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[id].ph_setpoint}
            controller_value={
              controllerSchedulePhase(parent, phase)
                    .nutrient_settings[id].ph_setpoint
            }
            controls={controls}
            disabled={!controller.access_rights?.scheduling?.write}
            num_digits={2}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[${id}].temp_setpoint`}
            show={'temp_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[id].temp_setpoint}
            controller_value={
              controllerSchedulePhase(parent, phase)
                    .nutrient_settings[id].temp_setpoint
            }
            controls={controls}
            disabled={!controller.access_rights?.scheduling?.write}
            type="float"
          />
        </SettingGroupGrid>

        {phase.nutrient_settings[id].pumps.map((pump, i) => (
          <Pump
            key={i}
            expand_id={`scheduling_pump_${i}`}
            id={id}
            index={i}
            name={pump.name}
            values={values}
            controls={controls}
            controller={controller}
            parent={parent}
            value_path_prefix={`schedule_values.scheduling_values[${index}].nutrient_settings[${id}].`}
            write_access={Boolean(controller.access_rights?.scheduling?.write)}
            edit_name={false}
          />
        ))}
      </>
    );
  } else {
    return <></>;
  }
}

function NutrientSettings({ index, values, phase, controls, controller, parent }) {
  if (
    values.schedule_meta.schedule_nutrients_enabled &&
    (Number(values.schedule_meta.schedule_nutrients_bitmap ?? 0) & 0x01) !== 0
  ) {
    return (
      <>
        <SettingGroupGrid controller={controller} id={'nutrient_solution'}>
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[0].ec_setpoint`}
            show={'ec_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[0].ec_setpoint}
            controller_value={
              controllerSchedulePhase(parent, phase)
                    .nutrient_settings[0].ec_setpoint
            }
            controls={controls}
            disabled={!controller.access_rights?.scheduling?.write}
            num_digits={2}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[0].ph_setpoint`}
            show={'ph_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[0].ph_setpoint}
            controller_value={
              controllerSchedulePhase(parent, phase)
                    .nutrient_settings[0].ph_setpoint
            }
            controls={controls}
            disabled={!controller.access_rights?.scheduling?.write}
            num_digits={2}
            type="float"
          />
          <ShowSetting
            id={`schedule_values.scheduling_values[${index}].nutrient_settings[0].temp_setpoint`}
            show={'temp_setpoint'}
            controller={controller}
            cur_value={phase.nutrient_settings[0].temp_setpoint}
            controller_value={
              controllerSchedulePhase(parent, phase)
                    .nutrient_settings[0].temp_setpoint
            }
            controls={controls}
            disabled={!controller.access_rights?.scheduling?.write}
            type="float"
          />
        </SettingGroupGrid>

        {phase.nutrient_settings[0].pumps.map((pump, i) => (
          <Pump
            key={i}
            expand_id={`scheduling_pump_${i}`}
            index={i}
            name={pump.name}
            values={values}
            controls={controls}
            controller={controller}
            parent={parent}
            value_path_prefix={`schedule_values.scheduling_values[${index}].nutrient_settings[0].`}
            write_access={Boolean(controller.access_rights?.scheduling?.write)}
            edit_name={false}
          />
        ))}
      </>
    );
  } else {
    return <></>;
  }
}

function MultipleIrrigationSettingsTabs({ index, values, phase, controls, controller, parent }) {
  const { t } = useTranslation();
  const irrigationIds = filterScheduleItemsByBitmap(
    Object.keys(phase.irrigation_settings || {}),
    values.schedule_meta.schedule_irrigations_bitmap,
    (irrigationId) => Number(irrigationId)
  );
  const [activeIrrigation, setActiveIrrigation] = React.useState(irrigationIds[0] || '');
  React.useEffect(() => {
    if (!irrigationIds.includes(activeIrrigation) && irrigationIds.length) {
      setActiveIrrigation(irrigationIds[0]);
    }
  }, [irrigationIds, activeIrrigation]);

  if (values.schedule_meta.schedule_irrigations_enabled && irrigationIds.length > 0) {
    return (
      <TabContext value={activeIrrigation}>
        <AppBar
          position="static"
          color="default"
          style={{ margin: '24px 24px 0', width: 'calc(100% - 48px)' }}
        >
          <Box display="flex" justifyContent="center" width="100%">
            <TabList
              variant="scrollable"
              scrollButtons="auto"
              onChange={(_, v) => setActiveIrrigation(v)}
            >
              {irrigationIds.map((iid, idx) => (
                <Tab
                  key={iid}
                  value={iid}
                  label={`${t('sub_zone')} ${Number(iid) + 1}`}
                  id={`phase-${index}-irrigation-tab-${idx}`}
                  aria-controls={`phase-${index}-irrigation-tabpanel-${idx}`}
                />
              ))}
            </TabList>
          </Box>
        </AppBar>

        {irrigationIds.map((iid, idx) => (
          <TabPanel
            key={iid}
            value={iid}
            id={`phase-${index}-irrigation-tabpanel-${idx}`}
            style={{ padding: 0 }}
          >
            <MultipleIrrigationSettings
              id={Number(iid)}
              index={index}
              values={values}
              phase={phase}
              controls={controls}
              controller={controller}
              parent={parent}
            />
          </TabPanel>
        ))}
      </TabContext>
    );
  } else {
    return <></>;
  }
}

function ScheduledIrrigationEvents({
  id,
  index,
  values,
  phase,
  eventPhase,
  controls,
  controller,
  parent
}) {
  const { t } = useTranslation();
  const controllerPhase = controllerSchedulePhase(parent, phase);
  const valuePath = `schedule_values.scheduling_values[${index}]`;
  const ordinarySettings = parent.settings.irrigation.irrigation_settings;
  const strategy = ordinarySettings[id]?.irrigation_strategy;
  const effectiveSettings = ordinarySettings.map((setting, settingIndex) =>
    settingIndex === id ? { ...setting, ...phase.irrigation_settings[id] } : setting
  );
  const mobile = useIsMobile();
  const { start: dayStart, end: dayEnd } = scheduledDayRange(
    parent.settings.timing,
    values.schedule_meta,
    phase
  );

  if (
    ((eventPhase === GCSPI_IRRIGATION_TIME_PHASE_P1 && isIrrigationTimeStrategy(strategy)) ||
      (eventPhase === GCSPI_IRRIGATION_TIME_PHASE_P2 && Number(strategy) === 2))
  ) {
    return (
      <IrrigationEventsEditor
        t={t}
        controller={controller}
        controls={controls}
        events={phase.irrigation_events || []}
        controllerEvents={controllerPhase?.irrigation_events || []}
        writeAccess={Boolean(controller.access_rights?.scheduling?.write)}
        irrigationSettings={effectiveSettings}
        irrigationIndex={id}
        phase={eventPhase}
        valuePath={`${valuePath}.irrigation_events`}
        expandId={`scheduling_irrigation_phase_${eventPhase}_events_${index}_${id}`}
        dayStartMinutes={dayStart}
        dayEndMinutes={dayEnd % 1440}
        mobile={mobile}
      />
    );
  }
  return <></>;
}

export function MultipleIrrigationSettings({
  id,
  index,
  values,
  phase,
  controls,
  controller,
  parent
}) {
  const { t } = useTranslation();
  const ordinary = parent.settings.irrigation?.irrigation_settings?.[id];
  // Scheduling rights do not imply permission to read the ordinary strategy.
  if (ordinary?.irrigation_strategy === undefined)
    return <Alert severity="info">{t('schedule_irrigation_unavailable')}</Alert>;
  const strategy = ordinary.irrigation_strategy;
  const current = phase.irrigation_settings[id];
  const saved =
    controllerSchedulePhase(parent, phase)?.irrigation_settings?.[
      id
    ] ?? {};
  const writeAccess = Boolean(controller.access_rights?.scheduling?.write);
  const path = (field) =>
    `schedule_values.scheduling_values[${index}].irrigation_settings[${id}].${field}`;
  const show = (field, extra = {}) => (
    <ShowSetting
      key={field}
      id={path(field)}
      show={field}
      controller={controller}
      cur_value={current[field]}
      controller_value={saved[field]}
      controls={controls}
      disabled={!writeAccess}
      {...extra}
    />
  );
  const adaptive = supportsAdaptiveIrrigation(controller, ordinary);
  const showWait = (field, irrigationPhase) => {
    const min = irrigationWaitMinimumMinutes(controller, ordinary, irrigationPhase, adaptive);
    return show(field, {
      min,
      max: 60,
      cur_value: Math.max(current[field], min),
      show: irrigationPhase === 2 && adaptive && ordinary.irrigation_p2_adaptive_enabled === 1
        ? 'irrigation_p2_wait_after_refill_minutes' : field,
      controls: { ...controls, setValue: (_, value, saveTemp = true) =>
        controls.setValue(path(field), Math.max(Number(value), min), saveTemp) }
    });
  };
  const toggle = (field, disabled = false) => (
    <ValueBox key={field} controller={controller} id="scheduling_">
      <Typography gutterBottom>
        <MuiSwitch
          checked={current[field] === 1 || current[field] === true}
          controller_value={saved[field]}
          color="primary"
          disabled={!writeAccess || disabled}
          inputProps={{ 'aria-label': t(field) }}
          onChange={(event) => controls.setValue(path(field), event.target.checked ? 1 : 0)}
        />
        <Desc id={field} />
      </Typography>
    </ValueBox>
  );
  const tankInfo = parent.settings.irrigation_tank_info ?? [];
  const tanks = tankInfo.filter(
    (tank) => isTankEnabled(tank) && tank.availible_in_irrigation_zones?.includes(Number(id))
  );
  const tankOptions = tanks.map((tank) => [tank.tank_id, tank.name]);
  tankOptions.push([255, 'tank_undefined']);
  if (
    current.selected_tank_id !== undefined &&
    !tankOptions.some(([tankId]) => tankId === current.selected_tank_id)
  )
    tankOptions.push([
      current.selected_tank_id,
      `${tankInfo.find((tank) => tank.tank_id === current.selected_tank_id)?.name ?? t('unknown')} (${current.selected_tank_id})`
    ]);
  const { start, end } = scheduledDayRange(parent.settings.timing, values.schedule_meta, phase);
  const dayClock = (value) => {
    let minute =
      typeof value === 'number'
        ? value
        : Number(value?.split(':')[0] ?? 0) * 60 + Number(value?.split(':')[1] ?? 0);
    if (minute < start) minute += 1440;
    return minute;
  };
  if (
    !values.schedule_meta.schedule_irrigations_enabled ||
    !(Number(values.schedule_meta.schedule_irrigations_bitmap ?? 0) & (1 << id))
  )
    return <></>;
  return (
    <>
      <ExpandableSettingsGroupGrid
        expand_id={`scheduling_more_irrigation_set_hl_${index}_${id}`}
        label={t('more_irrigation_set_hl')}
      >
        {toggle('enable_irrigation')}
        {Number(controller.type) === 5155 && (
          <ValueBox controller={controller} id="scheduling_">
            <Typography gutterBottom>
              <Desc id="selected_tank_id" />
            </Typography>
            <FlexSelect
              id={path('selected_tank_id')}
              controller={controller}
              value={current.selected_tank_id}
              controller_value={saved.selected_tank_id}
              mapping={tankOptions}
              use_index={false}
              disabled={!writeAccess}
              disabledValues={tankOptions
                .filter(
                  ([tankId]) => tankId !== 255 && !tanks.some((tank) => tank.tank_id === tankId)
                )
                .map(([tankId]) => tankId)}
              inputProps={{ 'aria-label': t('selected_tank_id') }}
              onChange={(event) =>
                controls.setValue(path('selected_tank_id'), Number(event.target.value))
              }
            />
          </ValueBox>
        )}
        {isIrrigationTimeStrategy(strategy) && (
          <ValueBox controller={controller} id="scheduling_">
            <Typography gutterBottom>
              <Desc id="irrigation_time_exec_gap_days" />
            </Typography>
            <EventGapSelect
              value={current.irrigation_time_exec_gap_days}
              disabled={!writeAccess}
              inputProps={{ 'aria-label': t('irrigation_time_exec_gap_days') }}
              onChange={(event) =>
                controls.setValue(path('irrigation_time_exec_gap_days'), Number(event.target.value))
              }
            />
          </ValueBox>
        )}
      </ExpandableSettingsGroupGrid>
      <IrrigationPhaseSettings phase={0} expandId={`scheduling_phase_0_activation_${index}_${id}`}>
        {!isIrrigationTimeStrategy(strategy) && show('irrigation_p0_dry_back_abs')}
        {(
          <IrrigationMoreSettings outside={true} expandId={`scheduling_phase_0_more_${index}_${id}`}>
            {show('irrigation_p0_phase_timeout_minutes', {
              disabled: !writeAccess || !current.irrigation_p0_enable ||
                (!isIrrigationTimeStrategy(strategy) && !current.irrigation_p0_enable_timeout)
            })}
            {toggle('irrigation_p0_enable')}
            {!isIrrigationTimeStrategy(strategy) &&
              toggle('irrigation_p0_enable_timeout', !current.irrigation_p0_enable)}
          </IrrigationMoreSettings>
        )}
      </IrrigationPhaseSettings>
      {(
        <IrrigationPhaseSettings phase={1} expandId={`scheduling_phase_1_ramp_up_${index}_${id}`}>
          {isIrrigationVwcStrategy(strategy) && show('irrigation_p1_target_vwc_abs')}
          <ScheduledIrrigationEvents
            outside={true}
            {...{ id, index, values, phase, controls, controller, parent }}
            eventPhase={GCSPI_IRRIGATION_TIME_PHASE_P1}
          />
          {!isIrrigationTimeStrategy(strategy) && !isIrrigationVwcSimpleStrategy(strategy) && (
            <IrrigationMoreSettings outside={true} expandId={`scheduling_phase_1_more_${index}_${id}`}>
              {supportsIrrigationReferenceInterval(controller) && Number(strategy) === 0 && (
                <IrrigationReferenceInterval
                  value={current.irrigation_p1_auto_vwc_exec_gap_days}
                  disabled={!writeAccess}
                  onChange={(event) => controls.setValue(
                    path('irrigation_p1_auto_vwc_exec_gap_days'), Number(event.target.value)
                  )}
                />
              )}
              {show('irrigation_p1_shot_size_vwc_rel')}
              {showWait('irrigation_p1_wait_after_shot_minutes', 1)}
            </IrrigationMoreSettings>
          )}
        </IrrigationPhaseSettings>
      )}
      <IrrigationPhaseSettings phase={2} expandId={`scheduling_phase_2_maintain_${index}_${id}`}>
        {isIrrigationAutoStrategy(strategy) &&
          show('irrigation_p2_offset_target_vwc', { type: 'float' })}
        {isIrrigationVwcStrategy(strategy) && show('irrigation_p2_target_vwc_abs')}
        {!isIrrigationTimeStrategy(strategy) &&
          show('irrigation_p2_dry_back_abs', { type: 'float' })}
        <ScheduledIrrigationEvents
          outside={true}
          {...{ id, index, values, phase, controls, controller, parent }}
          eventPhase={GCSPI_IRRIGATION_TIME_PHASE_P2}
        />
        {Number(strategy) === 3 && show('irrigation_p2_interval_on_duration_seconds', {
          convert_to_hhmmss: true, unit: 'sec'
        })}
        {Number(strategy) === 3 && show('irrigation_p2_interval_off_duration_minutes', {
          convert_to_hhmmss: true, unit: 'min'
        })}
        {show('irrigation_p2_end_before_sunset', {
          min: start,
          max: end,
          cur_value: dayClock(current.irrigation_p2_end_before_sunset),
          controller_value: dayClock(saved.irrigation_p2_end_before_sunset),
          convert_to_hhmmss: true,
          wrap_time_over_midnight: true,
          unit: 'min'
        })}
        {isIrrigationAdvancedVwcStrategy(strategy) && (
          <IrrigationMoreSettings outside={true} expandId={`scheduling_phase_2_more_${index}_${id}`}>
            {showWait('irrigation_p2_wait_after_shot_s', 2)}
          </IrrigationMoreSettings>
        )}
      </IrrigationPhaseSettings>
      {(
        <IrrigationPhaseSettings phase={3} expandId={`scheduling_phase_3_drydown_${index}_${id}`}>
          {!isIrrigationTimeStrategy(strategy) && show('irrigation_p3_min_vwc_abs')}
        </IrrigationPhaseSettings>
      )}
    </>
  );
}

function ScheduledIntervalTabs({ t, index, values, phase, controls, controller, parent }) {
  if (!values.schedule_meta.schedule_intervals_enabled) return <></>;
  const intervalIds = filterScheduleItemsByBitmap(
    [1, 2],
    values.schedule_meta.schedule_intervals_bitmap,
    (intervalId) => intervalId - 1
  );
  return (
    <StaticIntervalTabs
      ids={intervalIds}
      t={t}
      idPrefix={`phase-${index}-interval`}
      controller={controller}
      getValue={(id, field) => phase[`time_interval_${id}_${field}`]}
      onChange={(id, field, value) =>
        controls.setValue(
          `schedule_values.scheduling_values[${index}].time_interval_${id}_${field}`,
          value.toLocaleTimeString('de-DE')
        )
      }
    />
  );
}

export function ScheduledEventTabs({ t, index, values, phase, controls, controller, parent }) {
  if (!values.schedule_meta.schedule_events_enabled) return <></>;
  const eventIds = filterScheduleItemsByBitmap(
    [1, 2, 3, 4, 5, 6, 7, 8],
    values.schedule_meta.schedule_events_bitmap,
    (eventId) => eventId - 1
  );
  return (
    <StaticEventTabs
      ids={eventIds}
      t={t}
      idPrefix={`phase-${index}-event`}
      controller={controller}
      startSeconds={false}
      getValue={(id, field) =>
        field === 'exec_gap' ? phase[`time_event${id}_exec_gap`] : phase[`time_event${id}_${field}`]
      }
      getControllerValue={(id, field) =>
        field === 'exec_gap'
          ? controllerSchedulePhase(parent, phase)?.[
              `time_event${id}_exec_gap`
            ]
          : undefined
      }
      onTimeChange={(id, field, value) =>
        controls.setValue(
          `schedule_values.scheduling_values[${index}].time_event${id}_${field}`,
          value.toLocaleTimeString('de-DE')
        )
      }
      onGapChange={(id, value) =>
        controls.setValue(
          `schedule_values.scheduling_values[${index}].time_event${id}_exec_gap`,
          value
        )
      }
    />
  );
}

function ScheduledDynamicIntervalTabs({
  t,
  phaseIndex,
  timers,
  values,
  setValues,
  controls,
  controller
}) {
  if (!values.schedule_meta.schedule_intervals_enabled) return <></>;
  const selectedTimers = filterScheduleItemsByBitmap(
    timers.map((timer, timerIndex) => ({ timer, timerIndex })),
    values.schedule_meta.schedule_intervals_bitmap,
    ({ timerIndex }) => timerIndex
  );
  return (
    <IndexedSettingsTabs
      items={selectedTimers.map(({ timer, timerIndex }) => ({
        id: timer.id,
        label: `${t('io_interval')} ${timerIndex + 1}`,
        timer,
        timerIndex
      }))}
      idPrefix={`phase-${phaseIndex}-dynamic-interval`}
      contained={true}
      renderPanel={({ timer, timerIndex }) => {
        return (
          <IntervalTimerIndustrialPro
            index={timerIndex}
            id={timer.id}
            values={values}
            setValues={setValues}
            controls={controls}
            controller={controller}
            value_path_prefix={`schedule_values.scheduling_values[${phaseIndex}].`}
            allowDelete={false}
          />
        );
      }}
    />
  );
}

function ScheduledDynamicEventTabs({
  t,
  phaseIndex,
  timers,
  values,
  setValues,
  controls,
  controller
}) {
  if (!values.schedule_meta.schedule_events_enabled) return <></>;
  const selectedTimers = filterScheduleItemsByBitmap(
    timers.map((timer, timerIndex) => ({ timer, timerIndex })),
    values.schedule_meta.schedule_events_bitmap,
    ({ timerIndex }) => timerIndex
  );
  return (
    <IndexedSettingsTabs
      items={selectedTimers.map(({ timer, timerIndex }) => ({
        id: timer.id,
        label: `${t('io_event')} ${timerIndex + 1}`,
        timer,
        timerIndex
      }))}
      idPrefix={`phase-${phaseIndex}-dynamic-event`}
      contained={true}
      renderPanel={({ timer, timerIndex }) => {
        return (
          <EventTimerIndustrialPro
            id={timer.id}
            index={timerIndex}
            values={values}
            setValues={setValues}
            controls={controls}
            value_path_prefix={`schedule_values.scheduling_values[${phaseIndex}].`}
            allowDelete={false}
          />
        );
      }}
    />
  );
}
